import './context-menu.scss';

import { isEmpty, kebabCase } from 'lodash';
import {
  MouseEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ReactDOM from 'react-dom';

import { formatTestId } from '../../data/utils/string.utils';
import {
  ContextMenuHorizontalPosition,
  ContextMenuState,
  ContextMenuVerticalPosition,
} from '../../data/utils/use-context-menu';

const MARGIN = 20;
const TRESHHOLD = 5;

const getVerticalPosition = (state: ContextMenuState, element: DOMRect) => {
  let top = 0;

  if (state.data.vertical === ContextMenuVerticalPosition.Top) {
    top = Math.max(
      state.y - state.height / 2 - TRESHHOLD - element.height,
      MARGIN
    );
  } else if (state.data.vertical === ContextMenuVerticalPosition.Bottom) {
    top = Math.min(
      state.y + state.height / 2 + TRESHHOLD,
      window.innerHeight - MARGIN - element.height
    );
  } else {
    top = Math.max(
      Math.min(
        state.y - element.height / 2,
        window.innerHeight - element.height - MARGIN
      ),
      MARGIN
    );
  }

  return {
    top: `${top}px`,
  };
};

const getHorizontalPosition = (state: ContextMenuState, element: DOMRect) => {
  let left = 0;

  if (state.data.center) {
    if (state.data.horizontal === ContextMenuHorizontalPosition.Right) {
      left = Math.min(state.x, window.innerWidth - MARGIN - element.width);
    } else {
      left = Math.max(state.x - element.width, MARGIN);
    }
  } else if (!state.data.center) {
    if (state.data.horizontal === ContextMenuHorizontalPosition.Right) {
      left = Math.min(
        state.x - state.width / 2,
        window.innerWidth - MARGIN - element.width
      );
    } else {
      left = Math.max(state.x + state.width / 2 - element.width, MARGIN);
    }
  }

  return {
    left: `${left}px`,
  };
};

export enum ContextMenuItemIcon {
  None = 'None',
  Delete = 'Delete',
  Send = 'Send',
  Logout = 'Logout',
  Lock = 'Lock',
  Password = 'Lock-Password',
  User = 'User',
}

export type ContextMenuItem = {
  key: string;
  title: JSX.Element | string;
  icon?: ContextMenuItemIcon;
  disabled?: boolean;
  onClick: () => void | undefined;
};

type ContextMenuProps = {
  testId?: string;
  items: (ContextMenuItem | undefined | false)[];
  state: ContextMenuState | false;
  className?: string;
};

export function ContextMenu({
  testId,
  items,
  state,
  className,
}: ContextMenuProps) {
  const container = document.getElementById('portal-container');

  const getTestId = useCallback(
    (testIdType: string | string[] | null = null) =>
      formatTestId('context-menu', testId, testIdType),
    [testId]
  );

  const [style, setStyle] = useState<{
    top: string;
    left: string;
    opacity: number;
  }>();

  useEffect(() => {
    if (state) {
      const refElement = (state.ref as RefObject<HTMLDivElement>)?.current;

      if (refElement) {
        const element = refElement.getBoundingClientRect();

        setStyle({
          opacity: 1,
          ...getVerticalPosition(state, element),
          ...getHorizontalPosition(state, element),
        });
      }
    }

    return () => {
      setStyle(undefined);
    };
  }, [state, items]);

  const onHandleItem = useCallback(
    (e: MouseEvent<HTMLElement>, value: ContextMenuItem) => {
      e.preventDefault();
      e.stopPropagation();

      value.onClick?.();
    },
    []
  );

  const formattedItems = useMemo(
    () =>
      items.filter(option => option).map(option => option as ContextMenuItem),
    [items]
  );

  if (!container || !state || isEmpty(formattedItems)) return null;

  return ReactDOM.createPortal(
    <div
      data-test-id={getTestId()}
      className={`context-menu${
        className ? ` context-menu--${className}` : ``
      }${state.data.position ? ` context-menu--${state.data.position}` : ``}`}
      style={style}
      ref={state.ref}
    >
      <div
        data-test-id={getTestId('content')}
        className="context-menu__content"
      >
        <ul data-test-id={getTestId('list')}>
          {formattedItems.map(option => (
            <li key={option.key}>
              <button
                data-test-id={getTestId(['list', option.key])}
                type="button"
                disabled={option.disabled ?? !option.onClick}
                onClick={e => onHandleItem(e, option)}
              >
                {option.icon && option.icon !== ContextMenuItemIcon.None && (
                  <i
                    data-test-id={getTestId(['list', option.key, 'icon'])}
                    className={`icon ${kebabCase(option.icon)}`}
                  />
                )}

                <span data-test-id={getTestId(['list', option.key, 'text'])}>
                  {option.title}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>,
    container
  );
}
